import { useGetTotemsOfOrganizationLazyQuery } from 'data/__generated__';
import { getTotemFullName } from 'helpers/totemLabel';

import { PageBreadcrumb } from './PageBreadcrumb';
import { PageBreadcrumbSegment, type PageBreadcrumbDropdown } from './PageBreadcrumbSegment';

type Organization = { _id: string; name: string };

type Totem = {
    _id: string;
    name?: string | null;
    number: number;
    sites?: ReadonlyArray<{ _id: string; name: string }> | null;
};

type Site = {
    _id: string;
    name: string;
    microstoreColumnGroups?: ReadonlyArray<{ _id: string; name: string; positionInSite?: number | null }> | null;
};

type ColumnGroup = {
    _id: string;
    name: string;
    columns?: ReadonlyArray<{ _id: string; name: string; positionInColumnGroup?: number | null }> | null;
};

type Column = {
    _id: string;
    name: string;
    /** Only microstore columns have a details page; warehouse columns must not be linked. */
    microstoreColumn?: { _id: string } | null;
};

type Product = { _id: string; name: string };

type Props = {
    organization: Organization;
    totem?: Totem | null;
    site?: Site | null;
    columnGroup?: ColumnGroup | null;
    column?: Column | null;
    product?: Product | null;
    /** Set to true on `/site/:siteId/microstore-column-groups` to show "Microstore" as the current segment. */
    microstoreView?: boolean;
    /** Set to true on `/stocks` pages to show "Stocks" as the current segment after the deepest entity. */
    stocksView?: boolean;
    /** Backwards-compat: page-supplied dropdown for the totems-of-org list. If omitted, lazy-loaded on demand. */
    organizationTotemsDropdown?: PageBreadcrumbDropdown;
};

export const EntityPageBreadcrumb = ({
    organization,
    totem,
    site,
    columnGroup,
    column,
    product,
    microstoreView,
    stocksView,
    organizationTotemsDropdown,
}: Props) => {
    const current: 'organization' | 'totem' | 'site' | 'microstore' | 'columnGroup' | 'column' | 'stocks' | 'product' =
        product
            ? 'product'
            : stocksView
              ? 'stocks'
              : column
                ? 'column'
                : columnGroup
                  ? 'columnGroup'
                  : microstoreView && site
                    ? 'microstore'
                    : site
                      ? 'site'
                      : totem
                        ? 'totem'
                        : 'organization';

    const [getTotemsOfOrg, { data: lazyTotemsData, loading: lazyTotemsLoading, called: lazyTotemsCalled }] =
        useGetTotemsOfOrganizationLazyQuery();

    const totemsDropdown: PageBreadcrumbDropdown = organizationTotemsDropdown ?? {
        title: "Totems de l'organisation",
        isLoading: lazyTotemsLoading,
        items: (lazyTotemsData?.totemsOfOrganization ?? []).map((t) => ({
            label: getTotemFullName(t),
            to: `/totem/${t._id}`,
        })),
        emptyLabel: 'Aucun totem',
        onOpenChange: (open) => {
            if (open && !lazyTotemsCalled) {
                getTotemsOfOrg({ variables: { organizationId: organization._id } });
            }
        },
    };

    const sitesDropdown: PageBreadcrumbDropdown | undefined = totem?.sites
        ? {
              title: 'Sites du totem',
              items: [...totem.sites]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(({ _id, name }) => ({ label: name, to: `/site/${_id}` })),
              emptyLabel: 'Aucun site',
          }
        : undefined;

    const groupsDropdown: PageBreadcrumbDropdown[] | undefined = site
        ? [
              {
                  title: 'Stocks',
                  items: [{ label: "Vue d'ensemble", to: `/site/${site._id}/stocks` }],
              },
              {
                  title: 'Microstore',
                  items: [{ label: "Vue d'ensemble", to: `/site/${site._id}/microstore-column-groups` }],
              },
              {
                  title: 'Groupes de colonnes',
                  items: [...(site.microstoreColumnGroups ?? [])]
                      .sort((a, b) => (a.positionInSite ?? 0) - (b.positionInSite ?? 0))
                      .map(({ _id, name }) => ({ label: name, to: `/microstore-column-group/${_id}` })),
                  emptyLabel: 'Aucun groupe',
              },
          ]
        : undefined;

    const columnsDropdown: PageBreadcrumbDropdown | undefined = columnGroup?.columns
        ? {
              title: 'Colonnes du groupe',
              items: [...columnGroup.columns]
                  .sort((a, b) => (a.positionInColumnGroup ?? 0) - (b.positionInColumnGroup ?? 0))
                  .map(({ _id, name }) => ({ label: name, to: `/microstore-column/${_id}` })),
              emptyLabel: 'Aucune colonne',
          }
        : undefined;

    return (
        <PageBreadcrumb>
            <PageBreadcrumbSegment label="Organisations" to="/organizations" />

            <PageBreadcrumbSegment
                kicker="Organisation"
                label={organization.name}
                to={current === 'organization' ? undefined : `/organization/${organization._id}`}
                isCurrent={current === 'organization'}
            />

            {totem ? (
                <PageBreadcrumbSegment
                    kicker="Totem"
                    label={getTotemFullName(totem)}
                    to={current === 'totem' ? undefined : `/totem/${totem._id}`}
                    isCurrent={current === 'totem'}
                    dropdown={totemsDropdown}
                />
            ) : null}

            {site ? (
                <PageBreadcrumbSegment
                    kicker="Site"
                    label={site.name}
                    to={current === 'site' ? undefined : `/site/${site._id}`}
                    isCurrent={current === 'site'}
                    dropdown={sitesDropdown}
                />
            ) : null}

            {current === 'microstore' && site ? (
                <PageBreadcrumbSegment kicker="Vue" label="Microstore" isCurrent />
            ) : null}

            {columnGroup ? (
                <PageBreadcrumbSegment
                    kicker="Groupe"
                    label={columnGroup.name}
                    to={current === 'columnGroup' ? undefined : `/microstore-column-group/${columnGroup._id}`}
                    isCurrent={current === 'columnGroup'}
                    dropdown={groupsDropdown}
                />
            ) : null}

            {column ? (
                <PageBreadcrumbSegment
                    kicker="Colonne"
                    label={column.name}
                    to={
                        current !== 'column' && column.microstoreColumn
                            ? `/microstore-column/${column.microstoreColumn._id}`
                            : undefined
                    }
                    isCurrent={current === 'column'}
                    dropdown={columnsDropdown}
                />
            ) : null}

            {current === 'stocks' || current === 'product' ? (
                <PageBreadcrumbSegment
                    kicker="Vue"
                    label="Stocks"
                    to={current === 'product' && column ? `/column/${column._id}/stocks` : undefined}
                    isCurrent={current === 'stocks'}
                />
            ) : null}

            {product ? <PageBreadcrumbSegment kicker="Produit" label={product.name} isCurrent /> : null}

            {current === 'organization' ? (
                <PageBreadcrumbSegment kicker="Choisir" label="Totem" separator="arrow" dropdown={totemsDropdown} />
            ) : null}
            {current === 'totem' && sitesDropdown ? (
                <PageBreadcrumbSegment kicker="Choisir" label="Site" separator="arrow" dropdown={sitesDropdown} />
            ) : null}
            {current === 'site' && groupsDropdown ? (
                <PageBreadcrumbSegment kicker="Choisir" label="Vue" separator="arrow" dropdown={groupsDropdown} />
            ) : null}
            {current === 'microstore' && groupsDropdown ? (
                <PageBreadcrumbSegment kicker="Choisir" label="Groupe" separator="arrow" dropdown={groupsDropdown} />
            ) : null}
            {current === 'columnGroup' && columnsDropdown ? (
                <PageBreadcrumbSegment kicker="Choisir" label="Colonne" separator="arrow" dropdown={columnsDropdown} />
            ) : null}
        </PageBreadcrumb>
    );
};
